import { Component, effect, inject, input, signal } from '@angular/core';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormatLabelPipe } from "../../../../core/pipes/format-label/format-label.pipe";
import { OrganizationSettingModel } from '../../../../core/models/data/organization-setting.model';
import { IfHasPermissionDirective } from "../../../../core/directives/if-has-permission/if-has-permission.directive";
import { OrganizationPermission } from '../../../../core/enums/permissions.enums';
import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { LocationService } from '../../../../core/services/location.service';
import { TimeZoneUtilities } from '../../../../core/utilities/timezone.utilities';

@Component({
  selector: 'ms-organization-overview',
  imports: [FontAwesomeModule, FormatLabelPipe, IfHasPermissionDirective, DecimalPipe, TitleCasePipe, StatusBadgeComponent],
  templateUrl: './organization-profile-overview.component.html'
})
export class OrganizationProfileOverviewComponent {
  private locationService = inject(LocationService);

  public organization = input<OrganizationModel | undefined>(undefined);
  public organizationSettings = input<OrganizationSettingModel | undefined>(undefined);
  public isLoading = input<boolean>(false);

  public OrganizationPermission = OrganizationPermission;
  public timezoneDisplayName = signal<string>('');
  public isTimezoneLoading = signal<boolean>(false);

  // Guards against out-of-order responses: if organizationSettings() changes again (e.g. a fast
  // organization switch) before an in-flight getTimezones() call resolves, only the response for
  // the most recently requested timeZoneId is allowed to update state.
  private timezoneRequestId = 0;

  constructor() {
    effect(() => {
      this.loadTimezoneDisplayName(this.organizationSettings()?.timeZoneId);
    });
  }

  private async loadTimezoneDisplayName(timeZoneId: string | undefined): Promise<void> {
    const requestId = ++this.timezoneRequestId;

    if (!timeZoneId) {
      this.timezoneDisplayName.set('');
      return;
    }

    this.isTimezoneLoading.set(true);
    try {
      const result = await this.locationService.getTimezones();
      if (requestId !== this.timezoneRequestId) {
        return;
      }

      const timezone = result.data?.find(tz => tz.id === timeZoneId);
      this.timezoneDisplayName.set(timezone ? TimeZoneUtilities.stripUtcOffset(timezone.displayName) : timeZoneId);
    } finally {
      if (requestId === this.timezoneRequestId) {
        this.isTimezoneLoading.set(false);
      }
    }
  }
}
